import {
  FlyoutListMenuWidget, HorizontalOriginOption, IconLibraryIcon, MenuButtonWidget, MenuItemInfo, VerticalOriginOption
} from '@tableau/widgets';
import React from 'react';

import { ReactModalService } from '~commonInfra/dialogs/ReactModalService';
import { HomeUserUtils } from '~src/home/utils/HomeUserUtils';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { ExploreContentApi_ContentType } from '~src/spacesPlatform/models/IContent';

import { Messages } from '../../../../codegen/Localize';
import { CreateCollectionClickedSourceOptions,sendCreateCollectionClickedTelemetry } from '../../../../Collections/space/CollectionsTelemetry';
import { UploadFlowDialogOpts } from '../../../../uploadContent/components/ConnectedUploadFlowDialog';
import { UploadWorkbookDialogOpts } from '../../../../uploadContent/components/ConnectedUploadWorkbookDialog';
import { sendUploadFlowEntryPointTelemetry } from '../../../../uploadContent/utils/UploadFlowTelemetryEvents';
import { sendUploadWorkbookEntryPointTelemetry } from '../../../../uploadContent/utils/UploadWorkbookTelemetryEvents';
import { ExplorableItemType } from '../../../models/ContentType';
import { SpaceType } from '../../../state/SpaceInfo';
import { ExplorerStyles } from '../ExplorerStyles';
import { CreateContentType } from './CreateContentActionsList';
import { sendCreateContentOpenMenuTelemetry, sendCreateContentSelectItemTelemetry } from './CreateContentTelemetry';

export interface CreateContentButtonInfo {
  disabled: boolean;
  icon: (props: any) => React.ReactSVGElement | null;
  title: string;
  contentType: CreateContentType;
  isDivider: boolean;
}

export interface CreateContentButtonStateProps {
  createContentButtons: CreateContentButtonInfo[];
}

export interface CreateContentButtonDispatchProps {
  requestCreateContentButtonsInfo: () => void;
  createContent: (contentType: ExploreContentApi_ContentType | ExplorableItemType, projectId: string) => void;
}

export enum CreateContentButtonLocations {
  Home = 'Home',
  WelcomeChannel = 'WelcomeChannel',
  SiteSpace = 'SiteSpace',
  PersonalSpace = 'PersonalSpace',
  ExternalAssets = 'ExternalAssets',
}

export interface CreateContentButtonOwnProps {
  spaceType?: SpaceType;
  flyoutWidth?: number;
  projectId?: string;
  location?: CreateContentButtonLocations;
  useDarkTheme?: boolean;
  customButtonText?: string;
}

export type CreateContentButtonProps = CreateContentButtonStateProps & CreateContentButtonDispatchProps & CreateContentButtonOwnProps;

export interface CreateContentButtonState {
  showingCreateButtonsPanel: boolean;
}

export class CreateContentButton extends React.PureComponent<CreateContentButtonProps, CreateContentButtonState> {

  private createButtonRef: React.RefObject<HTMLButtonElement> = React.createRef();

  public constructor(props: CreateContentButtonProps) {
    super(props);
    this.state = { showingCreateButtonsPanel: false };
  }

  private showCreateButtonsPanel = (): void => {
    this.props.requestCreateContentButtonsInfo();
    this.setState({showingCreateButtonsPanel: !this.state.showingCreateButtonsPanel});
    sendCreateContentOpenMenuTelemetry(this.getTelemetryLocation());
  };

  private hideCreateButtons = (): void => {
    this.setState({showingCreateButtonsPanel: false});
  };

  private createButtonDisabled = (): boolean => {
    return this.props.createContentButtons.length === 0;
  };

  private shouldRenderCreateButtons = (): boolean => {
    return this.state.showingCreateButtonsPanel && !this.createButtonDisabled();
  };

  private getButtonKind = (): 'primary' | 'outlineDark' | 'outline' => {
    const siteTypeName = getSessionManagementServiceInstance().siteTypeName();
    if (HomeUserUtils.isGuidedOnboardingEnabled(siteTypeName) && this.props.location === CreateContentButtonLocations.Home) {
      return 'primary';
    }
    return this.props.useDarkTheme ? 'outlineDark' : 'outline';
  };

  public render(): JSX.Element {
    return <div>
      <MenuButtonWidget
        aria-expanded={this.shouldRenderCreateButtons()}
        aria-haspopup="menu"
        aria-controls={CreateButtonContentTestIds.CreateButtonsPanel}
        children={this.props.customButtonText ?? Messages.createContent_new()}
        onClick={this.showCreateButtonsPanel}
        disabled={this.createButtonDisabled()}
        kind={this.getButtonKind()}
        ref={this.createButtonRef}
        testId={CreateButtonContentTestIds.Button} />
      {this.shouldRenderCreateButtons() && this.renderCreateMenu()}
    </div>;
  }

  private renderCreateMenu = () => {
    return (
      <FlyoutListMenuWidget
        allowAutoPosition
        anchorEl={this.createButtonRef.current}
        anchorOrigin={{
          vertical: VerticalOriginOption.bottom,
          horizontal: HorizontalOriginOption.left
        }}
        className={ExplorerStyles.CreateContentMenuItemsStyleName}
        menuItemInfos={this.generateMenuItems()}
        onSelect={this.handleSelect}
        requestCloseAll={this.hideCreateButtons}
        id={CreateButtonContentTestIds.CreateButtonsPanel}
        testId={CreateButtonContentTestIds.CreateButtonsPanel}
        toggleEl={this.createButtonRef.current}
        menuStyle={{ minWidth: '150px', whiteSpace: 'nowrap' }}
      />
    );
  };

  private generateMenuItems = (): MenuItemInfo[] => {
    return this.props.createContentButtons.map(button => {
      if (button.isDivider) {
        return { isDivider: true };
      }

      let bigIcon: IconLibraryIcon;
      if (button.icon) {
        bigIcon = (props) => button.icon({ size: ExplorerStyles.buttonIconSize, color: props.color });
      }

      return {
        disabled: button.disabled,
        displayValue: button.title,
        icon: bigIcon,
        value: button.contentType,
      };
    });
  };

  private handleSelect = (value: string) => {
    this.hideCreateButtons();
    sendCreateContentSelectItemTelemetry(this.getTelemetryLocation(), value);
    if(value === 'collection') {
      sendCreateCollectionClickedTelemetry(this.props.spaceType, CreateCollectionClickedSourceOptions.NewMenu);
    }
    switch (value) {
      case 'workbookUpload': {
        ReactModalService.openCustom(UploadWorkbookDialogOpts(null, this.createButtonRef));
        sendUploadWorkbookEntryPointTelemetry('new-menu');
        break;
      }
      case 'flowUpload': {
        ReactModalService.openCustom(UploadFlowDialogOpts(null, this.createButtonRef));
        sendUploadFlowEntryPointTelemetry('new-menu');
        break;
      }
      default: {
        const contentType = value as ExploreContentApi_ContentType | ExplorableItemType;
        this.props.createContent(contentType, this.props.projectId);
      }
    }
  };

  private getTelemetryLocation() {
    return this.props.location ?? this.props.spaceType;
  }
}

export namespace CreateButtonContentTestIds {
  export const Button = 'explorer-create-content';
  export const CreateButtonsPanel = 'create-content-buttons-panel';
}
